// src/stores/map/mapStore.ts
//
// Phase 6 of STATE_MANAGEMENT_MIGRATION_PLAN.md
//
// Global Zustand store for map display, selection, navigation, and
// visualization state. Replaces the Redux `mapSlice` with field-level
// subscription narrowing via `subscribeWithSelector`.
//
// Design constraints (see MAP_STORE_MIGRATION_PLAN.md §3.6):
//   • Actions are synchronous domain transitions only.
//   • No Mapbox refs, no API calls, no imperative mutations.
//   • Side effects (debounced scheduling, Mapbox calls) live in
//     adapter / logic hooks — never in the store.
import { create } from "zustand";
import { subscribeWithSelector } from "zustand/middleware";
import { shallow } from "zustand/shallow";

import { EntityKind } from "models/entityKind";
import { DEFAULT_LATITUDE, DEFAULT_LONGITUDE, DEFAULT_ZOOM } from "models/mapDefaults";

import type {
  INetworkDepthLegend,
  MapState,
  MapStore,
  WellNetworkRequest
} from "./mapTypes";
import { mergeDisplaySettings } from "./mergeDisplaySettings";

// ── Initial Constants ────────────────────────────────────────────────────────

const INITIAL_NETWORK_DEPTH_LEGEND: INetworkDepthLegend = Object.freeze({
  legendItems: [],
  visible: false,
  title: "Network Depth"
});

const INITIAL_WELL_NETWORK_REQUEST: WellNetworkRequest = Object.freeze({
  wellIds: [],
  date: null,
  onlyPrimaryPath: true,
  hideConnectionsLessThanPercent: 0
});

// Exported for test assertions and logic hooks that need the canonical defaults.
export const initialState: MapState = {
  txnId: {
    id: "",
    facilityId: "",
    legend: { legendItems: [], title: "" },
    facilityLegend: { legendItems: [], title: "" },
    filterId: "",
    bBox: { sw: [], ne: [] }
  },

  selectedWells: {},
  selectedFacilities: {},
  attentionWells: [],
  jumpHighlightWells: [],
  typeLogWells: [],
  selectedRigFracLayer: null,
  hoveredRigFracId: null,
  rigFracFocusColorBy: undefined,
  rigFracColorByDetached: {},
  rigFracFilterOptions: {},

  displaySettings: {
    wellLineThickness: 2,
    wellDotThickness: 2,
    wellLabelSize: 9,
    facilityLabelSize: 9,
    facilityIconSize: 1,
    rigIconSize: 1,
    fracIconSize: 1,
    shapefileDetailsLabelSize: 9,
    shapefileNameLabelSize: 9,
    sectionLabelSize: 9,
    layerZoomSettings: {},
    labelDensity: {},
    layerFilterSettings: {},
    hiddenRigFracValues: {},
    rigFracColorSource: {},
    gridLabelSizes: {},
    layerStyleSettings: {}
  },
  layers: [],
  baseLayerResourcePlays: undefined,
  baseLayerFilterId: undefined,
  ipdbLayersLoaded: { geoModel: false, controlWells: false },
  geoModelLoading: false,
  isDebuggingMap: false,

  mapState: { lat: DEFAULT_LATITUDE, lng: DEFAULT_LONGITUDE, zoom: DEFAULT_ZOOM },
  viewLock: false,
  mapExtent: { type: "Polygon", coordinates: [] } as GeoJSON.Polygon,
  isMapMoving: false,
  mapMoverCount: 0,
  flyToGroup: undefined,
  flyToSpecificPoint: undefined,
  flyToExtent: undefined,
  attentionPolygon: undefined,
  exploitableArea: [],

  xdaInterceptData: { line: [], points: [] },
  xdaLineRequest: { line: [], wells: [] },

  networkDepthLegend: INITIAL_NETWORK_DEPTH_LEGEND,
  showNetworkOptions: false,
  highlightContributingWellsForFacility: null,
  wellNetworkMapRequest: INITIAL_WELL_NETWORK_REQUEST,
  isWellNetworkFilterActivated: false,
  networkEnvelope: null,
  salesEnvelope: null,

  hasToConfirmSize: false,
  facilityHasToConfirmSize: false,
  facilityOverrideSizeLimit: false,
  wellsConfirmationChoice: null,
  facilityConfirmationChoice: null
};

/**
 * The rig/frac-owned display settings a project View fully controls. On view
 * switch these are reset to their initial defaults BEFORE the incoming view's
 * saved settings are merged on top, so selecting a view that saved none of them
 * (e.g. a legacy view predating the rig/frac work) shows a clean slate instead
 * of silently inheriting the previously-selected view's rig/frac filters,
 * hidden values, colour source, or icon sizes.
 *
 * Scope note: only the rig/frac subset is reset. The other display settings
 * (label sizes, line/dot thickness) keep their merge-onto-current behavior
 * because they can carry the user's global default map settings, which a view
 * switch must not clobber. A NEW rig/frac display setting must be added here —
 * this is the one place the reset scope is deliberately explicit.
 */
const isEmptyRecord = (record: Maybe<object>): boolean =>
  !!record && Object.keys(record).length === 0;

const RIG_FRAC_DISPLAY_DEFAULTS = Object.freeze({
  layerFilterSettings: initialState.displaySettings.layerFilterSettings,
  hiddenRigFracValues: initialState.displaySettings.hiddenRigFracValues,
  rigFracColorSource: initialState.displaySettings.rigFracColorSource,
  rigIconSize: initialState.displaySettings.rigIconSize,
  fracIconSize: initialState.displaySettings.fracIconSize
});

// Element-wise equality for a layer's derived filter options (per field → values).
// `map idle` fires on every pan/zoom and recomputes identical facets in the steady
// state; this lets setRigFracFilterOptions return state by reference on a value no-op
// so it doesn't re-render every layer-settings subscriber.
function rigFracOptionsEqual(
  a: Readonly<Record<string, readonly string[]>> | undefined,
  b: Readonly<Record<string, readonly string[]>>
): boolean {
  if (!a) return false;
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  if (aKeys.length !== bKeys.length) return false;
  for (const key of bKeys) {
    const av = a[key];
    const bv = b[key];
    if (!av || av.length !== bv.length) return false;
    for (let i = 0; i < bv.length; i++) {
      if (av[i] !== bv[i]) return false;
    }
  }
  return true;
}

// Order- and duplicate-insensitive, so re-applying the same well set is a no-op
// rather than a store update.
function sameUwiSet(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false;
  const aSet = new Set(a);
  const bSet = new Set(b);
  if (aSet.size !== bSet.size) return false;
  for (const item of bSet) {
    if (!aSet.has(item)) return false;
  }
  return true;
}

// ── Store ────────────────────────────────────────────────────────────────────

export const useMapStore = create<MapStore>()(
  subscribeWithSelector((set, get) => ({
    ...initialState,

    // ── Transaction ────────────────────────────────────────────────────
    setTxnId: (incoming): void => {
      const current = get().txnId;
      // Skip update when tile-relevant IDs haven't changed — prevents
      // redundant MapboxWrapper re-renders.
      if (current?.id === incoming?.id && current?.facilityId === incoming?.facilityId) {
        return;
      }
      set({ txnId: incoming });
    },

    // ── Entity Selection ───────────────────────────────────────────────
    setSelectedWells: (wells): void => {
      set({ selectedWells: wells });
    },

    setSelectedFacilities: (facilities): void => {
      set({ selectedFacilities: facilities });
    },

    setSelectedEntities: (entityKind, entities): void => {
      if (entityKind === EntityKind.Well) {
        set({ selectedWells: entities });
      } else {
        set({ selectedFacilities: entities });
      }
    },

    setAttentionWells: (uwis): void => {
      if (!sameUwiSet(get().attentionWells, uwis)) {
        set({ attentionWells: uwis });
      }
    },

    setJumpHighlightWells: (uwis): void => {
      if (!sameUwiSet(get().jumpHighlightWells, uwis)) {
        set({ jumpHighlightWells: uwis });
      }
    },

    setTypeLogWells: (wells): void => {
      set({ typeLogWells: wells });
    },

    setSelectedRigFracLayer: (layer): void => {
      set({ selectedRigFracLayer: layer });
    },

    setHiddenRigFracValues: (layerId, values): void => {
      set((state) => ({
        displaySettings: {
          ...state.displaySettings,
          // Outer `?? {}` guards a write after a wholesale displaySettings
          // replace whose captured object predates this field.
          hiddenRigFracValues: {
            ...(state.displaySettings.hiddenRigFracValues ?? {}),
            [layerId]: values
          }
        }
      }));
    },

    clearHiddenRigFracValues: (): void => {
      set((state) => ({
        displaySettings: { ...state.displaySettings, hiddenRigFracValues: {} }
      }));
    },

    setRigFracColorSource: (layerId, source): void => {
      set((state) => ({
        displaySettings: {
          ...state.displaySettings,
          rigFracColorSource: {
            ...(state.displaySettings.rigFracColorSource ?? {}),
            [layerId]: source
          }
        }
      }));
    },

    clearRigFracColorSource: (): void => {
      set((state) => ({
        displaySettings: { ...state.displaySettings, rigFracColorSource: {} }
      }));
    },

    setHoveredRigFracId: (id): void => {
      set({ hoveredRigFracId: id });
    },

    setRigFracFocusColorBy: (field): void => {
      set((state) =>
        // Gated on the ATTRIBUTE alone, never on whether anything is detached.
        // Four focus properties resolve to "operator", so moving the well focus
        // between two operator-flavoured fields pushes the same attribute and
        // changes nothing on screen — it must not discard a hand-picked Color
        // By. A detachment survives until the pushed attribute really changes.
        state.rigFracFocusColorBy === field
          ? {}
          : { rigFracFocusColorBy: field, rigFracColorByDetached: {} }
      );
    },

    detachRigFracColorBy: (layerId): void => {
      set((state) =>
        state.rigFracColorByDetached[layerId]
          ? {}
          : {
              rigFracColorByDetached: {
                ...state.rigFracColorByDetached,
                [layerId]: true as const
              }
            }
      );
    },

    attachRigFracColorBy: (layerId): void => {
      set((state) => {
        if (!state.rigFracColorByDetached[layerId]) {
          return {};
        }
        const next = { ...state.rigFracColorByDetached };
        delete next[layerId];
        return { rigFracColorByDetached: next };
      });
    },

    clearRigFracFocusColorBy: (): void => {
      set((state) =>
        state.rigFracFocusColorBy === undefined &&
        Object.keys(state.rigFracColorByDetached).length === 0
          ? {}
          : { rigFracFocusColorBy: undefined, rigFracColorByDetached: {} }
      );
    },

    // ── Display Settings ───────────────────────────────────────────────
    setDisplaySettings: (settings): void => {
      set((state) => ({
        displaySettings:
          typeof settings === "function" ? settings(state.displaySettings) : settings
      }));
    },

    applyViewDisplaySettings: (saved): void => {
      set((state) => {
        // Reset the View-owned rig/frac and layer-style settings to their
        // defaults first, so a view that saved none of them clears the previous
        // view's state rather than inheriting it. mergeDisplaySettings then re-applies
        // whatever the incoming view actually saved (nothing, for a legacy
        // view) on top of that clean base.
        const base = {
          ...state.displaySettings,
          ...RIG_FRAC_DISPLAY_DEFAULTS,
          // Views own the layer style too: one that saved none loads the defaults.
          // Reuse an already-empty record so a no-op load stays a no-op.
          layerStyleSettings: isEmptyRecord(state.displaySettings.layerStyleSettings)
            ? state.displaySettings.layerStyleSettings
            : {}
        };
        const merged = mergeDisplaySettings(base, saved);
        // Value no-op: if the reset + merge produced settings identical to the
        // current ones (e.g. rig/frac already at defaults and saved adds
        // nothing), return state unchanged so no subscribers are notified
        // (FR-008). A shallow per-key compare — record categories are replaced
        // by reference, so reference equality per key is sufficient.
        if (shallow(merged, state.displaySettings)) {
          return state;
        }
        return { displaySettings: merged };
      });
    },

    setLabelDensity: (layer, value): void => {
      set((state) => ({
        displaySettings: {
          ...state.displaySettings,
          labelDensity: {
            ...state.displaySettings.labelDensity,
            [layer]: Math.max(0, Math.min(1, value))
          }
        }
      }));
    },

    setLayerFilter: (layerId, field, values): void => {
      set((state) => {
        // Outer `?? {}` guards a write after a wholesale setDisplaySettings
        // replace that left `layerFilterSettings` undefined (no defaults merge).
        const layerFilterSettings = state.displaySettings.layerFilterSettings ?? {};
        const currentLayerFilter = layerFilterSettings[layerId] ?? {};
        return {
          displaySettings: {
            ...state.displaySettings,
            layerFilterSettings: {
              ...layerFilterSettings,
              [layerId]: {
                ...currentLayerFilter,
                // Empty selection omits the field entirely so reads (`field in selections`
                // semantics via `?.length`) treat it as "show all" (FR-004).
                [field]: values.length ? values : undefined
              }
            }
          }
        };
      });
    },

    setRigFracFilterOptions: (layerId, options): void => {
      set((state) => {
        // Value no-op: idle recompute produced identical facets — return state by
        // reference so subscribers aren't notified.
        if (rigFracOptionsEqual(state.rigFracFilterOptions[layerId], options)) {
          return state;
        }
        return {
          rigFracFilterOptions: { ...state.rigFracFilterOptions, [layerId]: options }
        };
      });
    },

    setLayers: (layers): void => {
      set({ layers });
    },

    setBaseLayerResourcePlays: (plays): void => {
      set({ baseLayerResourcePlays: plays });
    },

    setBaseLayerFilterId: (filterId): void => {
      set({ baseLayerFilterId: filterId });
    },

    setIpdbGeoModelLoaded: (loaded): void => {
      set((state) => ({
        ipdbLayersLoaded: { ...state.ipdbLayersLoaded, geoModel: loaded }
      }));
    },

    setIpdbControlWellsLoaded: (loaded): void => {
      set((state) => ({
        ipdbLayersLoaded: {
          ...state.ipdbLayersLoaded,
          controlWells: loaded
        }
      }));
    },

    setGeoModelLoading: (loading): void => {
      set({ geoModelLoading: loading });
    },

    setIsDebuggingMap: (debugging): void => {
      set({ isDebuggingMap: debugging });
    },

    // ── Viewport / Navigation ──────────────────────────────────────────
    setMapState: (mapState): void => {
      set({ mapState });
    },

    setViewLock: (locked): void => {
      set({ viewLock: locked });
    },

    setMapExtent: (extent): void => {
      set({ mapExtent: extent });
    },

    beginMapMove: (): void => {
      // One count per moving map widget, not one shared true/false. With two
      // maps on screen a last-write-wins flag would let the second map's stop
      // announce that the first map, still moving, had settled.
      const mapMoverCount = get().mapMoverCount + 1;
      set({ mapMoverCount, isMapMoving: true });
    },

    endMapMove: (): void => {
      // Floored, so a stop that was never claimed cannot drive the count
      // negative and leave the next claim reading as no movement at all.
      const mapMoverCount = Math.max(0, get().mapMoverCount - 1);
      set({ mapMoverCount, isMapMoving: mapMoverCount > 0 });
    },

    setFlyToGroup: (group): void => {
      set({ flyToGroup: group });
    },

    setFlyToSpecificPoint: (point): void => {
      set({ flyToSpecificPoint: point });
    },

    setFlyToExtent: (extent, options): void => {
      // Strictly increasing stamp: two picks landing in the same millisecond
      // must still read as two distinct commands to the consuming maps.
      set((s) => ({
        flyToExtent: extent
          ? {
              extent,
              at: Math.max(Date.now(), (s.flyToExtent?.at ?? 0) + 1),
              options
            }
          : undefined
      }));
    },

    setAttentionPolygon: (polygon): void => {
      set({ attentionPolygon: polygon });
    },

    setExploitableArea: (area): void => {
      set({ exploitableArea: area });
    },

    // ── XDA Cross-Section ──────────────────────────────────────────────
    setXdaInterceptData: (data): void => {
      set({ xdaInterceptData: data });
    },

    setXdaLineRequest: (request): void => {
      set({ xdaLineRequest: request });
    },

    // ── Midstream Network ──────────────────────────────────────────────
    setNetworkDepthLegend: (legend): void => {
      set({ networkDepthLegend: legend });
    },

    clearNetworkDepthLegend: (): void => {
      set({ networkDepthLegend: INITIAL_NETWORK_DEPTH_LEGEND });
    },

    setShowNetworkOptions: (show): void => {
      set({ showNetworkOptions: show });
    },

    setHighlightContributingWellsForFacility: (facilityId): void => {
      set({ highlightContributingWellsForFacility: facilityId });
    },

    clearHighlightContributingWellsForFacility: (): void => {
      set({ highlightContributingWellsForFacility: null });
    },

    setWellNetworkMapRequest: (request): void => {
      set({ wellNetworkMapRequest: request });
    },

    clearWellNetworkMapRequest: (): void => {
      set({ wellNetworkMapRequest: INITIAL_WELL_NETWORK_REQUEST });
    },

    setWellNetworkFilterActivated: (activated): void => {
      set({ isWellNetworkFilterActivated: activated });
    },

    setNetworkEnvelope: (envelope): void => {
      set({ networkEnvelope: envelope });
    },

    setSalesEnvelope: (envelope): void => {
      set({ salesEnvelope: envelope });
    },

    // ── Series Size Confirmation ───────────────────────────────────────
    setHasToConfirmSize: (confirm): void => {
      set({ hasToConfirmSize: confirm });
    },

    setFacilityHasToConfirmSize: (confirm): void => {
      set({ facilityHasToConfirmSize: confirm });
    },

    setFacilityOverrideSizeLimit: (override): void => {
      set({ facilityOverrideSizeLimit: override });
    },

    setWellsConfirmationChoice: (choice): void => {
      set({ wellsConfirmationChoice: choice });
    },

    setFacilityConfirmationChoice: (choice): void => {
      set({ facilityConfirmationChoice: choice });
    },

    // ── Lifecycle ──────────────────────────────────────────────────────
    reset: (): void => {
      set(initialState);
    }
  }))
);

// Expose the store for Playwright E2E tests (stripped by bundler in production builds).
if (process.env.NODE_ENV !== "production") {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (window as any).__mapStore = useMapStore;
}
